import React, { useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import CampaignIcon from '@mui/icons-material/Campaign'
import AddIcon from '@mui/icons-material/Add'
import {
  useListMarketingCampaignRequestsQuery, useCreateMarketingCampaignRequestMutation,
  useUpdateMarketingCampaignRequestStatusMutation,
} from '../../redux/growthosMarketingCampaignApiSlice'
import { GX_BORDER, GX_INK_500, GX_INK_700, GX_COLORS, GxSectionCard } from './GrowthOsUi'

const headCellSx = {
  px: 1.5, py: 1.5, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
  color: GX_INK_500, borderBottom: `1px solid ${GX_BORDER}`, whiteSpace: 'nowrap',
}
const bodyCellSx = { px: 1.5, py: 1, color: GX_INK_700, borderBottom: `1px solid ${GX_BORDER}`, fontSize: 12, whiteSpace: 'nowrap' }

const REQUEST_STATUSES = ['PENDING', 'IN_PROGRESS', 'DONE', 'REJECTED']

const fmtWhen = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Shared across every GrowthOS Marketing sub-page — the real app shows the same "Request a
// Campaign" list on LinkedIn/ABM, Email Marketing, etc., not one per channel.
export default function GxRequestCampaignSection() {
  const { data: requests } = useListMarketingCampaignRequestsQuery()
  const [createRequest, createState] = useCreateMarketingCampaignRequestMutation()
  const [updateStatus] = useUpdateMarketingCampaignRequestStatusMutation()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [brief, setBrief] = useState('')

  const submit = async () => {
    if (!name.trim()) return
    await createRequest({ campaignName: name.trim(), brief: brief.trim() })
    setOpen(false); setName(''); setBrief('')
  }

  return (
    <GxSectionCard title="Request a Campaign" icon={<CampaignIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.purple}
      right={<Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ textTransform: 'none', borderRadius: '10px' }}>New request</Button>}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: GX_INK_700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>Campaign Requests</Typography>
      {!requests?.length ? (
        <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>No campaign requests yet. Submit one above.</Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Requested By</TableCell>
                <TableCell sx={headCellSx}>Campaign</TableCell>
                <TableCell sx={headCellSx}>Brief</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell sx={headCellSx}>Updated By</TableCell>
                <TableCell sx={headCellSx}>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={bodyCellSx}>{r.requestedByName}</TableCell>
                  <TableCell sx={bodyCellSx}>{r.campaignName}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, whiteSpace: 'normal', maxWidth: 320 }}>{r.brief}</TableCell>
                  <TableCell sx={bodyCellSx}>
                    <TextField select size="small" value={r.status} onChange={(e) => updateStatus({ id: r.id, status: e.target.value })}
                      sx={{ minWidth: 130 }}>
                      {REQUEST_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>{r.updatedByName || '—'}</TableCell>
                  <TableCell sx={bodyCellSx}>{fmtWhen(r.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request a Campaign</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Campaign name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus />
          <TextField label="Brief" value={brief} onChange={(e) => setBrief(e.target.value)} fullWidth multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={!name.trim() || createState.isLoading} onClick={submit} sx={{ textTransform: 'none' }}>
            {createState.isLoading ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </GxSectionCard>
  )
}
